/* =============================================================
   brands-marquee.js
   Place in: assets/brands-marquee.js
   ============================================================= */

(function () {
  'use strict';

  const DESKTOP_MQ = window.matchMedia('(min-width: 750px)');

  class BrandsMarquee {
    /**
     * @param {HTMLElement} el  The <section class="brands-marquee"> element
     */
    constructor(el) {
      this.el         = el;
      this.trackWrap  = el.querySelector('.brands-marquee__track-wrap');
      this.track      = el.querySelector('.brands-marquee__track');
      this.listOrig   = el.querySelector('.brands-marquee__list--original');
      this.listClone  = el.querySelector('.brands-marquee__list--clone');

      if (!this.trackWrap || !this.track || !this.listOrig || !this.listClone) return;

      /* Animation state */
      this._raf       = null;
      this._position  = 0;
      this._listWidth = 0;
      this._running   = false;

      this._readSettings();

      /* Wait for all images inside the marquee to load so widths are accurate */
      this._waitForImages().then(() => {
        this._measure();
        this._init();
      });

      /* Re-measure + re-init on resize */
      this._onResize = this._debounce(() => {
        this._measure();
        this._init();
      }, 250);
      window.addEventListener('resize', this._onResize);

      /* Shopify theme editor */
      this._onSectionLoadBound = this._onSectionLoad.bind(this);
      document.addEventListener('shopify:section:load', this._onSectionLoadBound);
    }

    /* ── Read data attributes ───────────────────────────────── */
    _readSettings() {
      this.autoScroll = this.el.dataset.autoScroll === 'true';
      /* Fallback: if desktop auto-scroll above is off, scroll anyway once
         the logos genuinely don't all fit next to the heading. */
      this.autoScrollOnOverflow = this.el.dataset.autoScrollOverflow === 'true';
      this.direction  = this.el.dataset.scrollDirection || 'left'; // 'left' | 'right'
      /* Speed in seconds → convert to px/frame later via _measure() */
      this.speedSeconds = parseFloat(this.el.dataset.scrollSpeed) || 30;
    }

    /* ── Resolve after every <img> inside the track has loaded ─ */
    _waitForImages() {
      const imgs = Array.from(this.track.querySelectorAll('img'));
      if (!imgs.length) return Promise.resolve();

      const promises = imgs.map((img) => {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return new Promise((resolve) => {
          img.addEventListener('load',  resolve, { once: true });
          img.addEventListener('error', resolve, { once: true }); // don't stall on broken imgs
        });
      });

      return Promise.all(promises);
    }

    /* ── Measure the original list width once images are ready ─ */
    _measure() {
      /*
       * scrollWidth counts content + padding but NOT the trailing gap.
       * CSS `gap` only inserts space *between* flex children, so after the
       * last item there is no gap — meaning the clone starts immediately
       * after the last logo with no breathing room, causing a visible jump
       * on reset.
       *
       * Fix: read the computed gap for the current breakpoint and add it
       * once so the reset point lands exactly where the next loop begins.
       *
       *   totalWidth = scrollWidth + 1 × gap
       *
       * We read `--bm-gap-desktop` / `--bm-gap-mobile` from the section's
       * inline style (set by Liquid) so we always use the live value,
       * including after a settings change in the editor.
       */
      const styles    = getComputedStyle(this.el);
      const isDesktop = DESKTOP_MQ.matches;
      const gapVar    = isDesktop ? '--bm-gap-desktop' : '--bm-gap-mobile';
      const gap       = parseFloat(styles.getPropertyValue(gapVar)) || 0;

      this._listWidth = this.listOrig.scrollWidth + gap;

      /*
       * Available width is the track-wrap's own box — flex:1 on desktop,
       * so it's genuinely "whatever's left next to the heading," not the
       * full section width. Used only to decide whether to start scrolling
       * via the overflow fallback, not for the scroll math itself.
       */
      this._availableWidth = this.trackWrap.clientWidth;

      if (!this._listWidth) return;

      /*
       * Pixel-per-frame speed:
       * We want to travel exactly one full listWidth in speedSeconds.
       * At 60 fps: px/frame = listWidth / (speedSeconds * 60)
       */
      this._pxPerFrame = this._listWidth / (this.speedSeconds * 60);

      /*
       * Initialise position so right-direction starts from the clone side
       * and snaps to the same visual starting point as left.
       */
      if (this.direction === 'right' && this._position === 0) {
        this._position = -this._listWidth;
      }
    }

    /* ── Decide whether to start or stop ────────────────────── */
    _init() {
      this._readSettings();

      const isDesktop = DESKTOP_MQ.matches;

      /*
       * The "Auto-scroll on desktop" checkbox only gates desktop.
       * Mobile always scrolls when there are logos to show.
       */
      let shouldScroll = this._listWidth > 0 && (isDesktop ? this.autoScroll : true);

      /*
       * Overflow fallback — only relevant on desktop, and only when the
       * main desktop toggle above is off (if it's on, we're already
       * scrolling). If the logos genuinely don't fit in the space next to
       * the heading, start scrolling anyway rather than leaving them
       * silently clipped with no way to see the rest.
       */
      if (!shouldScroll && isDesktop && this.autoScrollOnOverflow) {
        const contentWidth = this.listOrig.scrollWidth;
        if (contentWidth > this._availableWidth) {
          shouldScroll = true;
        }
      }

      if (shouldScroll) {
        this._startScroll();
      } else {
        this._stopScroll();
        /* Reset visual position */
        this.track.style.transform = '';
        this._position = 0;
      }
    }

    /* ── rAF scroll loop ────────────────────────────────────── */
    _startScroll() {
      if (this._running) return;
      this._running = true;
      this._tick();
    }

    _tick() {
      if (!this._running) return;

      if (this.direction === 'right') {
        /* Move right: position goes from -listWidth → 0, then resets */
        this._position += this._pxPerFrame;
        if (this._position >= 0) {
          this._position = -this._listWidth;
        }
      } else {
        /* Move left (default): position goes from 0 → -listWidth, then resets */
        this._position -= this._pxPerFrame;
        if (this._position <= -this._listWidth) {
          this._position = 0;
        }
      }

      this.track.style.transform = `translateX(${this._position}px)`;
      this._raf = requestAnimationFrame(this._tick.bind(this));
    }

    _stopScroll() {
      this._running = false;
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
    }

    /* ── Shopify editor: section reloaded ───────────────────── */
    _onSectionLoad(event) {
      if (event.target.contains(this.el)) {
        this._stopScroll();
        this._position = 0;
        this._readSettings();
        this._waitForImages().then(() => {
          this._measure();
          this._init();
        });
      }
    }

    /* ── Debounce helper ────────────────────────────────────── */
    _debounce(fn, wait) {
      let timer;
      return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    }

    /* ── Destroy (editor section:unload) ────────────────────── */
    destroy() {
      this._stopScroll();
      this.track.style.transform = '';
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('shopify:section:load', this._onSectionLoadBound);
    }
  }

  /* ── Boot ─────────────────────────────────────────────────── */

  function initAll() {
    document.querySelectorAll('.brands-marquee').forEach((el) => {
      if (!el._brandsMarquee) {
        el._brandsMarquee = new BrandsMarquee(el);
      }
    });
  }

  /* Use window load so all images are decoded and laid out */
  if (document.readyState === 'complete') {
    initAll();
  } else {
    window.addEventListener('load', initAll, { once: true });
  }

  /* Shopify editor: section dropped onto the page */
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.brands-marquee');
    if (section) {
      if (section._brandsMarquee) section._brandsMarquee.destroy();
      section._brandsMarquee = new BrandsMarquee(section);
    }
  });

  /* Shopify editor: section removed */
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.brands-marquee');
    if (section && section._brandsMarquee) {
      section._brandsMarquee.destroy();
      delete section._brandsMarquee;
    }
  });

})();